import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Check, Crown, Zap, ShoppingBag, AlertTriangle, Mail } from "lucide-react";

const SUPPORT_EMAIL = "arienterprises50@gmail.com";

// Razorpay surfaces domain-registration issues via a few different messages.
// This checks against the ones we've observed while awaiting approval.
function isDomainNotRegisteredError(message) {
    if (!message) return false;
    const m = String(message).toLowerCase();
    return (
        m.includes("domain") ||
        m.includes("not registered") ||
        m.includes("website is not") ||
        m.includes("merchant is not") ||
        m.includes("international")
    );
}

const ICONS = { single: ShoppingBag, bulk: Zap, premium: Crown };
const TAGLINES = {
    single: "Test the waters",
    bulk: "Best for active sellers",
    premium: "Scale your store",
};

const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve(true));
            existing.addEventListener("error", () => resolve(false));
            return;
        }
        const script = document.createElement("script");
        script.src = RAZORPAY_SCRIPT_SRC;
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

function formatINR(amount) {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(amount);
}

export default function Pricing() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [packages, setPackages] = useState([]);
    const [loadingId, setLoadingId] = useState(null);
    const [paymentError, setPaymentError] = useState(null);

    useEffect(() => {
        api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
        // Preload Razorpay script in the background for faster first checkout
        loadRazorpayScript();
    }, []);

    const handlePaymentSuccess = useCallback(async (rzpResponse, pkg) => {
        try {
            const { data } = await api.post("/payments/verify", {
                razorpay_order_id: rzpResponse.razorpay_order_id,
                razorpay_payment_id: rzpResponse.razorpay_payment_id,
                razorpay_signature: rzpResponse.razorpay_signature,
            });
            toast.success(`${data.credits} credit${data.credits > 1 ? "s" : ""} added!`);
            await refresh();
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
        } finally {
            setLoadingId(null);
        }
    }, [navigate, refresh]);

    // Poll the transaction while the Razorpay modal is open. This is critical for UPI/QR flows
    // where the modal's `handler()` callback sometimes doesn't fire after payment.captured —
    // the webhook fires reliably though, so we detect the paid state server-side and close the modal.
    const startTxnPoll = useCallback((orderId, rzpInstance) => {
        const startedAt = Date.now();
        const maxDurationMs = 8 * 60 * 1000; // stop polling after 8 minutes
        const intervalId = setInterval(async () => {
            if (Date.now() - startedAt > maxDurationMs) {
                clearInterval(intervalId);
                return;
            }
            try {
                const { data } = await api.get(`/payments/transactions/${orderId}`);
                if (data.payment_status === "paid") {
                    clearInterval(intervalId);
                    try { rzpInstance && rzpInstance.close(); } catch (_) { /* modal may already be closed */ }
                    toast.success(`${data.credits} credit${data.credits > 1 ? "s" : ""} added!`);
                    await refresh();
                    setLoadingId(null);
                    navigate("/dashboard");
                }
            } catch (_) {
                // Silent — keep polling; auth issues will settle in a moment
            }
        }, 3000);
        return intervalId;
    }, [navigate, refresh]);


    const buy = async (pkg) => {
        if (!user) {
            navigate("/login");
            return;
        }
        setLoadingId(pkg.id);
        setPaymentError(null);

        const sdkLoaded = await loadRazorpayScript();
        if (!sdkLoaded) {
            toast.error("Could not load Razorpay. Check your internet connection.");
            setLoadingId(null);
            return;
        }

        let order;
        try {
            const { data } = await api.post("/payments/checkout", { package_id: pkg.id });
            order = data;
        } catch (err) {
            const detail = err.response?.data?.detail || err.message;
            if (isDomainNotRegisteredError(detail)) {
                setPaymentError("banner");
            } else {
                toast.error(detail || "Could not create order");
            }
            setLoadingId(null);
            return;
        }

        const options = {
            key: order.razorpay_key_id,
            amount: order.amount,
            currency: order.currency,
            name: "listcraft",
            description: `${order.package_name} — ${pkg.credits} credit${pkg.credits > 1 ? "s" : ""}`,
            order_id: order.order_id,
            handler: (rzpResponse) => handlePaymentSuccess(rzpResponse, pkg),
            prefill: {
                name: user?.name || "",
                email: user?.email || "",
            },
            theme: {
                color: "#bef264",
                backdrop_color: "#0A0A0A",
            },
            modal: {
                ondismiss: () => setLoadingId(null),
            },
        };

        try {
            const rzp = new window.Razorpay(options);
            let pollId = null;
            rzp.on("payment.failed", (resp) => {
                const description = resp.error?.description || resp.error?.reason || "Payment failed";
                if (isDomainNotRegisteredError(description) || isDomainNotRegisteredError(resp.error?.code)) {
                    setPaymentError("banner");
                } else {
                    toast.error(description);
                }
                if (pollId) clearInterval(pollId);
                setLoadingId(null);
            });
            rzp.open();
            // Kick off txn polling as a safety net for UPI/QR flows where handler() may not fire
            pollId = startTxnPoll(order.order_id, rzp);
            // Clear poll on dismiss too so we don't keep hitting the API after user cancels
            options.modal = options.modal || {};
            const priorDismiss = options.modal.ondismiss;
            options.modal.ondismiss = () => {
                if (pollId) clearInterval(pollId);
                if (typeof priorDismiss === "function") priorDismiss();
            };
        } catch (err) {
            if (isDomainNotRegisteredError(err?.message)) {
                setPaymentError("banner");
            } else {
                toast.error("Could not open Razorpay checkout");
            }
            setLoadingId(null);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-6 py-16" data-testid="pricing-page">
            <div className="text-center mb-14">
                <div className="overline mb-3">Pricing</div>
                <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight mb-4">
                    Pay as you list<span className="text-accent">.</span>
                </h1>
                <p className="text-lg text-zinc-500">No subscriptions. Buy credits and use them whenever.</p>
            </div>

            {paymentError && (
                <div
                    role="alert"
                    className="mb-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-5 py-4"
                    data-testid="payment-error-banner"
                >
                    <div className="flex-shrink-0 w-9 h-9 rounded-md border border-amber-500/30 bg-amber-500/10 flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4 text-amber-400" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-heading text-sm font-semibold text-amber-100 mb-1">
                            We&apos;re finalizing payment setup
                        </div>
                        <p className="text-sm text-amber-100/70">
                            Please try again in a few hours, or email us for early access.
                        </p>
                    </div>
                    <a
                        href={`mailto:${SUPPORT_EMAIL}?subject=Early%20access%20to%20listcraft%20checkout`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-amber-400/40 bg-amber-400/10 text-amber-100 text-xs font-mono uppercase tracking-widest hover:bg-amber-400/20 transition-colors"
                        data-testid="payment-error-contact"
                    >
                        <Mail className="w-3.5 h-3.5" strokeWidth={1.75} />
                        Email us
                    </a>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="packages-grid">
                {packages.map((pkg) => {
                    const Icon = ICONS[pkg.id] || ShoppingBag;
                    const isPremium = pkg.premium;
                    return (
                        <div
                            key={pkg.id}
                            className={`surface p-8 flex flex-col relative ${isPremium ? "border-accent/40 glow-accent" : ""}`}
                            data-testid={`package-${pkg.id}`}
                        >
                            {isPremium && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-accent text-bg text-[10px] font-mono uppercase tracking-widest">
                                    Most popular
                                </div>
                            )}
                            <div className="w-10 h-10 rounded-md border border-white/10 bg-surface-2 flex items-center justify-center mb-5">
                                <Icon className={`w-4 h-4 ${isPremium ? "text-accent" : "text-zinc-400"}`} strokeWidth={1.75} />
                            </div>
                            <div className="overline mb-2">{TAGLINES[pkg.id] || ""}</div>
                            <h3 className="font-heading text-2xl font-semibold tracking-tight mb-3">{pkg.name}</h3>
                            <div className="flex items-baseline gap-1 mb-1">
                                <span className="font-heading font-bold text-5xl tracking-tighter text-zinc-100">₹{formatINR(pkg.amount)}</span>
                                <span className="font-mono text-xs text-zinc-500">{pkg.currency}</span>
                            </div>
                            <div className="font-mono text-xs text-zinc-500 mb-6">
                                {pkg.credits} {pkg.credits === 1 ? "credit" : "credits"} · ₹{formatINR(Math.round(pkg.amount / pkg.credits))}/listing
                            </div>
                            <ul className="space-y-2.5 mb-8 text-sm flex-1">
                                <li className="flex items-start gap-2 text-zinc-300"><Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" /> {pkg.credits} AI-generated listing{pkg.credits > 1 ? "s" : ""}</li>
                                <li className="flex items-start gap-2 text-zinc-300"><Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" /> Amazon, Shopify, Etsy, eBay formats</li>
                                <li className="flex items-start gap-2 text-zinc-300"><Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" /> SEO titles, meta tags, bullets, variants</li>
                                <li className="flex items-start gap-2 text-zinc-300"><Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" /> Unlimited copy &amp; export</li>
                                {isPremium && (
                                    <>
                                        <li className="flex items-start gap-2 text-accent font-medium"><Crown className="w-4 h-4 mt-0.5 flex-shrink-0" /> Priority processing queue</li>
                                        <li className="flex items-start gap-2 text-accent font-medium"><Crown className="w-4 h-4 mt-0.5 flex-shrink-0" /> Premium badge on account</li>
                                    </>
                                )}
                            </ul>
                            <button
                                onClick={() => buy(pkg)}
                                disabled={loadingId === pkg.id}
                                className={isPremium ? "btn-primary w-full" : "btn-ghost w-full"}
                                data-testid={`buy-${pkg.id}`}
                            >
                                {loadingId === pkg.id ? "Loading..." : `Buy ${pkg.name}`}
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="mt-14 text-center font-mono text-xs text-zinc-600">
                Secure payments by Razorpay · Credits never expire · UPI, cards, netbanking & wallets
            </div>
        </div>
    );
}
